import { FilterFilled } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  Empty,
  Input,
  message,
  Pagination,
  Popover,
  Spin,
} from 'antd';
import React, { useState } from 'react';
import styled from 'styled-components';

import { primaryColor } from '../../constants/COLORS';
import { fetchAPI } from '../../utils/api';
import { EntityForFilter } from './types';

type Props = {
  selected: number[];
  onChange: (payload: number[]) => void;
};

const emptyResp = (page = 1, per_page = 10, total = 0, loading = true) => ({
  items: [],
  page,
  per_page,
  total,
  loading,
});

const FilterMultipleEntity = (props: Props) => {
  const [filter, setFilter] = useState({
    q: '',
    page: 1,
  });
  const [selected, setSelected] = useState<number[]>(props.selected);

  const entities = useQuery<{
    items: EntityForFilter[];
    page: number;
    per_page: number;
    total: number;
    loading: boolean;
  }>(
    ['entity/list-for-filter', filter],
    async () => {
      try {
        const resp = await fetchAPI('entity/list-for-filter', {
          method: 'POST',
          payload: {
            q: filter.q,
            page: filter.page,
            per_page: 10,
          },
        });

        return resp;
      } catch (error) {
        message.error('Failed get entity');
        return emptyResp(filter.page, 10, 0, false);
      }
    },
    {
      staleTime: 10 * 60 * 1000,
      placeholderData: emptyResp(filter.page, 10, 0, true),
    }
  );

  return (
    <Popover
      trigger="click"
      title={null}
      placement="rightTop"
      content={
        <ContentWrapper>
          <Input.Search
            placeholder="Search Entity"
            onSearch={val => setFilter({ q: val, page: 1 })}
            style={{ marginBottom: 8, display: 'block' }}
            loading={entities.isLoading}
            disabled={entities.isLoading}
            enterButton
            allowClear
          />
          <Spin spinning={entities.isLoading}>
            {!entities.data?.total ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <ul style={{ padding: 0, margin: '0 0 15px', listStyle: 'none' }}>
                {entities.data.items.map(item => {
                  const entityId = Number(item.id);
                  return (
                    <li key={String(item.id)}>
                      <Checkbox
                        value={entityId}
                        checked={selected.includes(entityId)}
                        onChange={e => {
                          if (e.target?.checked) {
                            setSelected(sel => [...sel, entityId]);
                          } else {
                            setSelected(sel =>
                              sel.filter(selId => !!selId && selId !== entityId)
                            );
                          }
                        }}
                      >
                        {item.name}
                      </Checkbox>
                    </li>
                  );
                })}
              </ul>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Button
                type="link"
                onClick={() => {
                  setSelected([]);
                  props.onChange([]);
                }}
              >
                Reset
              </Button>
              <Pagination
                current={filter.page}
                pageSize={10}
                total={entities.data?.total || 0}
                onChange={page => setFilter(f => ({ ...f, page }))}
                showSizeChanger={false}
                size="small"
                style={{ textAlign: 'right' }}
              />
              <Button type="primary" onClick={() => props.onChange(selected)}>
                Apply
              </Button>
            </div>
          </Spin>
        </ContentWrapper>
      }
    >
      <p
        style={{
          margin: '0 0 0 15px',
          color: primaryColor,
          fontWeight: 'bold',
          cursor: 'pointer',
          marginRight: 15,
        }}
      >
        Filter Entity <FilterFilled />
      </p>
    </Popover>
  );
};

const ContentWrapper = styled.div``;

export default FilterMultipleEntity;
